import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import SeleccionContext from "../../../context/Seleccion/SeleccionContext";
import InputFileCustomized from "../kit-mdtw/InputFileCustomized";
import IconLoading from "../kit-svg/IconLoading";

/** Marca que el perfil usa para una columna descartada a propósito. */
const SIN_USO = "__SIN_USO__";

/** Campos virtuales que el motor reparte y no deben ofrecerse dos veces. */
const CAMPOS_EXTRA: string[] = ["NOMBRE COMPLETO", "APELLIDOS", "EDAD"];

/** El correo se puede escribir a mano, así que hay que validarlo. */
const correoValido = (valor: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor);

interface ICargaNominaIngesta {
  token: string;
  getToken: Function;
  uploadModalVisible: boolean;
  setUploadModalVisible: Function;
  busquedaId?: string;
  correoReclutador?: string;
}

interface IColumna {
  indice: number;
  cabecera: string;
  destino: string | null;
  tipo: string | null;
  nivel: string;
  confianza: number;
  requiereRevision: boolean;
  ejemplos: string[];
  motivo: string;
}

type Paso = 1 | 2 | 3;

const CargaNominaIngesta = (props: ICargaNominaIngesta) => {
  const { uploadModalVisible, setUploadModalVisible, busquedaId, correoReclutador } = props;
  const {
    analizarNomina,
    confirmarNomina,
    cargarNominaPortales,
    estadoCargaNomina,
    listProcesosActivos,
    getBusquedaProcesos,
    listaUsuarios,
    getListaUsuarios,
    token,
  } = useContext(SeleccionContext);

  /* Proceso destino: ningún portal lo trae en el archivo, así que se elige
     aquí. Sin esto la carga no sabe a qué búsqueda asignar los candidatos. */
  const [procesoDestino, setProcesoDestino] = useState<string | undefined>(busquedaId);
  const [procesoOptions, setProcesoOptions] = useState<
    { code: string; name: string }[]
  >([]);

  /* Reclutador dueño de los candidatos. Por defecto el de la sesión, pero
     cambiable: quien sube el archivo no siempre es quien lleva el proceso. */
  const [reclutador, setReclutador] = useState<string | undefined>(correoReclutador);
  const [reclutadorOptions, setReclutadorOptions] = useState<
    { code: string; name: string }[]
  >([]);

  /* Destino por candidato: { [numeroDeFila]: { proceso, reclutador } }.
     Lo que no se define aqui hereda el destino global de la barra. */
  const [destinoPorFila, setDestinoPorFila] = useState<
    Record<number, { proceso?: string; reclutador?: string }>
  >({});

  const [paso, setPaso] = useState<Paso>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [analisis, setAnalisis] = useState<any>(null);
  const [resumenFinal, setResumenFinal] = useState<any>(null);
  const [correcciones, setCorrecciones] = useState<Record<string, string>>({});
  const [guardarPerfil, setGuardarPerfil] = useState<boolean>(true);
  const [cancelTokenSource, setCancelTokenSource] = useState(axios.CancelToken.source());

  const [documento, setDocumento] = useState<any>({
    documento: { value: undefined, valid: false, fechaExpiracion: "" },
  });

  const archivo = documento?.documento?.value;

  /* Se piden los procesos activos una vez y se arma el desplegable, con el
     mismo formato que usa la pantalla de solicitar candidato. */
  useEffect(() => {
    if (listProcesosActivos?.data) {
      const options: { code: string; name: string }[] = [];

      listProcesosActivos.data.forEach(({ clienteNombre, grupos }: any) => {
        grupos?.forEach(({ procesos }: any) => {
          procesos?.forEach(({ busquedaId: id, cargoNombre }: any) => {
            options.push({ code: id, name: `${id} · ${clienteNombre} · ${cargoNombre}` });
          });
        });
      });

      setProcesoOptions(options);
      return;
    }

    if (token) {
      getBusquedaProcesos({ token, filtros: {}, setIsErrorTable: () => undefined });
    }
  }, [listProcesosActivos, token]);

  /* Reclutadores. Se preselecciona el de la sesión buscándolo por usuarioId. */
  useEffect(() => {
    if (!listaUsuarios?.length) {
      token && getListaUsuarios({ token });
      return;
    }

    const conCorreo = listaUsuarios.filter((item: any) => item.usuarioEmail);

    setReclutadorOptions(
      conCorreo.map((item: any) => ({
        code: item.usuarioEmail,
        name: `${item.usuarioNombre ?? item.usuarioEmail}`,
      }))
    );

    if (!reclutador) {
      const sesion: string | null = sessionStorage.getItem("usuarioId");
      const propio = conCorreo.find((item: any) => item.usuarioId === sesion);
      propio && setReclutador(propio.usuarioEmail);
    }
  }, [listaUsuarios, token]);
  const columnas: IColumna[] = analisis?.mapeo?.columnas ?? [];
  const filas: any[] = analisis?.previsualizacion ?? [];
  const faltantes: string[] = analisis?.mapeo?.faltanObligatorias ?? [];

  /**
   * Destinos que ofrece el selector. Salen del canon que el motor reconoció en
   * este archivo, más los campos que quedaron sin usar en otras columnas.
   */
  const opcionesDestino: string[] = useMemo(() => {
    const encontrados = new Set<string>();

    columnas.forEach((columna: IColumna) => {
      if (columna.destino && columna.destino !== SIN_USO) {
        encontrados.add(columna.destino);
      }
    });

    (analisis?.camposCanon ?? []).forEach((campo: string) => encontrados.add(campo));

    return Array.from(encontrados)
      .filter((campo: string) => !CAMPOS_EXTRA.includes(campo))
      .sort();
  }, [columnas, analisis]);

  /* Las opciones se construyen una sola vez. Se reusan en los 33 selects de
     la cabecera, en el select de proceso de cada fila y en el datalist, que
     antes se recreaban enteros en cada tecla que se escribia en el modal. */
  const optionsDestino = useMemo(
    () =>
      opcionesDestino.map((destino: string) => (
        <option key={destino} value={destino}>
          {destino}
        </option>
      )),
    [opcionesDestino]
  );

  const optionsProceso = useMemo(
    () =>
      procesoOptions.map((opcion) => (
        <option key={opcion.code} value={opcion.code}>
          {opcion.name}
        </option>
      )),
    [procesoOptions]
  );

  const optionsReclutador = useMemo(
    () =>
      reclutadorOptions.map((opcion) => (
        <option key={opcion.code} value={opcion.code}>
          {opcion.name}
        </option>
      )),
    [reclutadorOptions]
  );

  const destinoDe = (columna: IColumna): string =>
    correcciones[columna.cabecera] ?? columna.destino ?? SIN_USO;

  const porRevisar: number = columnas.filter(
    (columna: IColumna) => destinoDe(columna) === SIN_USO || columna.requiereRevision
  ).length;

  const cerrar = () => {
    cancelTokenSource.cancel("Cancelado por el usuario");
    setUploadModalVisible(false);
  };

  const reiniciar = () => {
    setPaso(1);
    setAnalisis(null);
    setResumenFinal(null);
    setCorrecciones({});
    setDocumento({ documento: { value: undefined, valid: false, fechaExpiracion: "" } });
  };

  /* El spinner lo gobierna este componente de punta a punta y NO cada llamada.
     Cuando cada una lo apagaba al terminar, entre que se apagaba y que el paso
     avanzaba habia un render intermedio con la vista ANTERIOR: el modal volvia
     un instante a la pantalla de archivo antes de mostrar el resultado, y eso
     es el parpadeo. Manteniendolo encendido hasta despues de setPaso, la vista
     nueva es lo primero que se ve al apagarlo. */
  const analizar = async () => {
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);
    setIsLoading(true);

    try {
      const data = await analizarNomina({
        file: archivo,
        busquedaId: procesoDestino ?? busquedaId,
        correoReclutador: reclutador,
        cancelToken: source.token,
      });

      if (!data) {
        return;
      }

      setAnalisis(data);
      setPaso(2);
    } finally {
      setIsLoading(false);
    }
  };

  /* Tres llamadas encadenadas apagaban y encendian el spinner tres veces, y en
     cada hueco reaparecia la tabla de columnas. Ahora es un solo ciclo. */
  const importar = async () => {
    setIsLoading(true);

    try {
      const confirmado = await confirmarNomina({
        cargaId: analisis.cargaId,
        correcciones,
        guardarPerfil,
        nombrePerfil: analisis.mapeo?.perfil ?? undefined,
        busquedaId: procesoDestino,
        correoReclutador: reclutador,
        destinoPorFila,
      });

      if (!confirmado) {
        return;
      }

      const cargado = await cargarNominaPortales({ cargaId: analisis.cargaId });

      if (!cargado) {
        return;
      }

      const estado = await estadoCargaNomina({ cargaId: analisis.cargaId });
      /* `estado` aporta las filas y los totales guardados; `cargado` aporta el
         desglose (creadas / actualizadas / sin cambios) que `estado` no devuelve.
         Los dos traen una clave `resumen`, asi que hay que fusionarla: con un
         spread plano la de `estado` pisaba a la otra y el panel mostraba ceros. */
      setResumenFinal({
        ...cargado,
        ...(estado ?? {}),
        resumen: { ...(cargado?.resumen ?? {}), ...(estado?.resumen ?? {}) },
      });
      setPaso(3);
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarDestino = (cabecera: string, destino: string) => {
    const siguientes: Record<string, string> = { ...correcciones, [cabecera]: destino };

    /* Un campo del canon no puede recibir dos columnas: al armar la fila
       canonica el backend hace `values[destino] = valor`, asi que la segunda
       columna pisa a la primera sin avisar y se pierde un dato. Cuando el
       destino elegido ya estaba tomado, se libera al que lo tenia en vez de
       dejar dos columnas peleando por el mismo campo. */
    if (destino !== SIN_USO) {
      columnas.forEach((columna: IColumna) => {
        if (columna.cabecera !== cabecera && destinoDe(columna) === destino) {
          siguientes[columna.cabecera] = SIN_USO;
        }
      });
    }

    setCorrecciones(siguientes);
  };

  /* Destino por candidato. Vacío significa "usa el de la barra de arriba";
     así el caso común (todos al mismo proceso) no obliga a tocar 150 filas. */
  const procesoDeFila = (fila: number): string =>
    destinoPorFila[fila]?.proceso ?? "";

  const reclutadorDeFila = (fila: number): string =>
    destinoPorFila[fila]?.reclutador ?? "";

  const cambiarDestinoFila = (
    fila: number,
    campo: "proceso" | "reclutador",
    valor: string
  ) => {
    setDestinoPorFila({
      ...destinoPorFila,
      [fila]: { ...(destinoPorFila[fila] ?? {}), [campo]: valor || undefined },
    });
  };

  /** Aplica el valor de la primera fila al resto: atajo para repartos parciales. */
  const replicarDestino = (campo: "proceso" | "reclutador") => {
    const primera = filas[0];

    if (!primera) {
      return;
    }

    const valor: string =
      campo === "proceso" ? procesoDeFila(primera.fila) : reclutadorDeFila(primera.fila);

    if (!valor) {
      return;
    }

    const todas = { ...destinoPorFila };
    filas.forEach((f: any) => {
      todas[f.fila] = { ...(todas[f.fila] ?? {}), [campo]: valor };
    });
    setDestinoPorFila(todas);
  };

  /** "Configurar todas las columnas como.." de la pantalla de referencia. */
  const aplicarATodas = (destino: string) => {
    if (!destino) {
      return;
    }

    const todas: Record<string, string> = {};
    columnas.forEach((columna: IColumna) => {
      todas[columna.cabecera] = destino;
    });
    setCorrecciones(todas);
  };

  /* -------------------------------------------------------------- paso 1 -- */

  const vistaArchivo = () => (
    <>
      <p className="pl-8">
        Sube el archivo del portal tal como lo descargaste. No hace falta pasarlo a la
        plantilla.
      </p>

      <div className="col-12 content-section flex pl-8">
        <div className="col-10 flex" style={{ float: "left" }}>
          <InputFileCustomized
            layer={"Archivo (*):"}
            placeholder={"Seleccione archivo"}
            placeholderImg={true}
            required={true}
            iconFile={false}
            iconDel={false}
            maxSizeMb={10}
            errorPosition="down"
            date={false}
            onPropertyChange="documento"
            onPermaValues={documento}
            onValueChange={(value: any) =>
              setDocumento({
                documento: {
                  value: value.documento.value[0],
                  valid: value.documento.valid,
                  fechaExpiracion: "",
                },
              })
            }
            leyenda={""}
            requerido={false}
            datoRequerido={false}
            accept=".xlsx, .xls, .csv"
          />
        </div>
      </div>

      <p className="pCargaNomina pl-8">
        Acepta archivos .xlsx, .xls y .csv de cualquier portal. Los de{" "}
        <strong>Pandapé</strong> se reconocen automáticamente; con otros formatos vas a
        revisar el mapeo de columnas la primera vez, y el sistema lo recuerda para las
        siguientes.
      </p>
    </>
  );

  /* ------------------------------------------- paso 2: configurar columnas -- */

  const vistaColumnas = () => (
    <div className="ingesta-config">
      <h3 className="ingesta-config-titulo">Configura columnas del archivo</h3>
      <p className="ingesta-config-sub">
        Asigna un campo a las columnas del archivo o también puedes ignorar las que no
        necesites.
      </p>

      {/* Destino de la carga. Ningún portal trae el proceso en el archivo, así
          que se elige aquí, con el archivo ya analizado a la vista. */}
      <div className="ingesta-destino">
        <div className="ingesta-destino-campo">
          <label>
            Proceso de destino <span className="req">*</span>
          </label>
          <select
            className={`ingesta-select ${procesoDestino ? "" : "revisar"}`}
            value={procesoDestino ?? ""}
            onChange={(e) => setProcesoDestino(e.target.value || undefined)}
          >
            <option value="">Selecciona el proceso…</option>
            {optionsProceso}
          </select>
          {analisis?.referenciaAviso && !procesoDestino && (
            <span className="ingesta-hint">
              El archivo viene del aviso <b>{analisis.referenciaAviso}</b>
            </span>
          )}
        </div>

        {/* Quien carga no es necesariamente el reclutador dueño del proceso,
            así que se puede cambiar. Por defecto viene el de la sesión. */}
        <div className="ingesta-destino-campo">
          <label>
            Reclutador <span className="req">*</span>
          </label>
          {/* Campo abierto con sugerencias: si la lista de usuarios todavía no
              cargó, o el reclutador no está en ella, el correo se escribe. */}
          <input
            className={`ingesta-select ${
              reclutador && correoValido(reclutador) ? "" : "revisar"
            }`}
            list="ingesta-reclutadores"
            type="email"
            placeholder="correo@team-work.cl"
            value={reclutador ?? ""}
            onChange={(e) => setReclutador(e.target.value.trim() || undefined)}
          />
          <datalist id="ingesta-reclutadores">
            {optionsReclutador}
          </datalist>
          <span className="ingesta-hint">
            {reclutadorOptions.length > 0
              ? `Elige uno de los ${reclutadorOptions.length} usuarios o escribe el correo`
              : "Escribe el correo del reclutador"}
            {reclutador && !correoValido(reclutador) && (
              <b className="ingesta-celda-error"> · correo no válido</b>
            )}
          </span>
        </div>
      </div>

      <div className="ingesta-config-todas">
        <label>Configurar todas las columnas como:</label>
        <select
          className="ingesta-select"
          defaultValue=""
          onChange={(e) => aplicarATodas(e.target.value)}
        >
          <option value="">Seleccionar todas como..</option>
          <option value={SIN_USO}>Ignorar columna</option>
          {optionsDestino}
        </select>
      </div>

      <div className="ingesta-banner">
        <b>{columnas.length} columnas detectadas.</b>{" "}
        {columnas.length - porRevisar} asignadas automáticamente
        {porRevisar > 0 ? `; ${porRevisar} necesitan tu confirmación.` : "."}
        {analisis?.mapeo?.perfil && (
          <span>
            {" "}
            Perfil aplicado: <b>{analisis.mapeo.perfil}</b>.
          </span>
        )}
        {analisis?.referenciaAviso && (
          <span>
            {" "}
            Aviso del archivo: <b>{analisis.referenciaAviso}</b>.
          </span>
        )}
      </div>

      {faltantes.length > 0 && (
        <div className="ingesta-banner error">
          Faltan columnas obligatorias: <b>{faltantes.join(", ")}</b>.
        </div>
      )}

      {/* Una columna por cada columna del archivo: arriba el selector, abajo
          los datos reales ya estandarizados. */}
      <div className="ingesta-grid-scroll">
        <table className="ingesta-grid">
          <thead>
            <tr className="ingesta-grid-selects">
              <th className="ingesta-grid-fila">&nbsp;</th>
              {/* Estos dos huecos emparejan las columnas Proceso y Reclutador de
                  la fila de abajo. Sin ellos esta fila tiene dos celdas menos y
                  cada select aparece sobre la cabecera equivocada. */}
              <th className="ingesta-col-destino">&nbsp;</th>
              <th className="ingesta-col-destino">&nbsp;</th>
              {columnas.map((columna: IColumna) => (
                <th key={columna.indice}>
                  <select
                    className={`ingesta-select ${
                      destinoDe(columna) === SIN_USO
                        ? "ignorada"
                        : columna.requiereRevision
                        ? "revisar"
                        : ""
                    }`}
                    value={destinoDe(columna)}
                    onChange={(e) => cambiarDestino(columna.cabecera, e.target.value)}
                    title={columna.motivo}
                  >
                    <option value={SIN_USO}>Ignorar columna</option>
                    {optionsDestino}
                  </select>
                </th>
              ))}
            </tr>
            <tr className="ingesta-grid-cabeceras">
              <th className="ingesta-grid-fila">Fila</th>
              <th className="ingesta-col-destino">
                Proceso{" "}
                <button
                  type="button"
                  className="ingesta-replicar"
                  title="Copiar el de la primera fila a todas"
                  onClick={() => replicarDestino("proceso")}
                >
                  ↓ todas
                </button>
              </th>
              <th className="ingesta-col-destino">
                Reclutador{" "}
                <button
                  type="button"
                  className="ingesta-replicar"
                  title="Copiar el de la primera fila a todas"
                  onClick={() => replicarDestino("reclutador")}
                >
                  ↓ todas
                </button>
              </th>
              {columnas.map((columna: IColumna) => (
                <th key={columna.indice} title={columna.cabecera}>
                  {columna.cabecera}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filas.map((fila: any) => (
              <tr key={fila.fila}>
                <td className="ingesta-grid-fila">{fila.fila}</td>

                {/* Destino de esta fila. Vacío = hereda el de la barra. */}
                <td className="ingesta-col-destino">
                  <select
                    className="ingesta-select"
                    value={procesoDeFila(fila.fila)}
                    onChange={(e) =>
                      cambiarDestinoFila(fila.fila, "proceso", e.target.value)
                    }
                  >
                    <option value="">
                      {procesoDestino ? `(${procesoDestino})` : "— sin definir —"}
                    </option>
                    {optionsProceso}
                  </select>
                </td>

                <td className="ingesta-col-destino">
                  <input
                    className="ingesta-select"
                    list="ingesta-reclutadores"
                    type="email"
                    placeholder={reclutador ? `(${reclutador})` : "correo@team-work.cl"}
                    value={reclutadorDeFila(fila.fila)}
                    onChange={(e) =>
                      cambiarDestinoFila(fila.fila, "reclutador", e.target.value.trim())
                    }
                  />
                </td>

                {columnas.map((columna: IColumna) => {
                  const destino = destinoDe(columna);
                  const ignorada = destino === SIN_USO;
                  const conError = (fila.errores ?? []).some(
                    (e: any) => e.field === destino
                  );
                  const corregido = (fila.corregidos ?? []).includes(destino);
                  const valor = ignorada ? null : fila.valores?.[destino];

                  return (
                    <td
                      key={columna.indice}
                      className={
                        ignorada
                          ? "ingesta-celda-ignorada"
                          : conError
                          ? "ingesta-celda-error"
                          : corregido
                          ? "ingesta-celda-corregida"
                          : ""
                      }
                    >
                      {ignorada ? "—" : String(valor ?? "—")}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="ingesta-pie-config">
        <label className="ingesta-check">
          <input
            type="checkbox"
            checked={guardarPerfil}
            onChange={(e) => setGuardarPerfil(e.target.checked)}
          />
          <span>
            Recordar este mapeo para futuros archivos de{" "}
            <b>{analisis?.mapeo?.perfil ?? "este portal"}</b>
          </span>
        </label>

        <div className="ingesta-tally">
          <span className="tal ok">{analisis?.resumen?.clean ?? 0} sin cambios</span>
          <span className="tal aviso">
            {analisis?.resumen?.corrected ?? 0} estandarizadas
          </span>
          <span className="tal error">{analisis?.resumen?.rejected ?? 0} con error</span>
        </div>
      </div>

      <p className="ingesta-leyenda">
        <b className="ingesta-celda-corregida">Ámbar</b>: el valor se estandarizó al
        cargarlo. <b className="ingesta-celda-error">Rojo</b>: la fila no se puede cargar.
        Se muestran las primeras {filas.length} filas de{" "}
        {analisis?.archivo?.totalFilas ?? filas.length}.
      </p>
    </div>
  );

  /* -------------------------------------------------------------- paso 3 -- */

  /* Los cinco estados que puede traer una fila (RowState del backend). Antes
     sólo se distinguía RECHAZADA del resto, así que CON OBSERVACION se veía
     igual que CARGADA y no había forma de encontrarlas de un vistazo. */
  const tonoEstado = (estado: string): string => {
    if (estado === "CARGADA") return "ok";
    if (estado === "RECHAZADA") return "error";
    if (estado === "CON OBSERVACION") return "aviso";
    return "neutro";
  };

  const vistaResumen = () => {
    const r = resumenFinal?.resumen ?? {};
    const detalle: any[] = resumenFinal?.filas ?? [];

    const tiles: { valor: number; etiqueta: string; tono: string }[] = [
      { valor: r.total ?? detalle.length, etiqueta: "Filas", tono: "neutro" },
      { valor: r.creadas ?? 0, etiqueta: "Creadas", tono: "ok" },
      { valor: r.actualizadas ?? 0, etiqueta: "Actualizadas", tono: "aviso" },
      { valor: r.sinCambios ?? 0, etiqueta: "Sin cambios", tono: "neutro" },
      { valor: r.rechazadas ?? 0, etiqueta: "Rechazadas", tono: "error" },
    ];

    return (
      <div className="ingesta-resumen">
        <p className="ingesta-resumen-contexto">
          {analisis?.archivo?.nombre && <b>{analisis.archivo.nombre}</b>}
          {resumenFinal?.proceso && <> · proceso {resumenFinal.proceso}</>}
          {reclutador && <> · {reclutador}</>}
        </p>

        <div className="ingesta-resumen-tiles">
          {tiles.map((t) => (
            <div key={t.etiqueta} className={`ingesta-tile ${t.tono}`}>
              <span className="ingesta-tile-valor">{t.valor}</span>
              <span className="ingesta-tile-etiqueta">{t.etiqueta}</span>
            </div>
          ))}
        </div>

        {r.asignadasAlProceso !== undefined && (
          <p className="ingesta-resumen-asignacion">
            <b>{r.asignadasAlProceso}</b> asignadas al proceso
            {r.sinAsignar ? <> · <b>{r.sinAsignar}</b> sin asignar</> : null}
          </p>
        )}

        <div className="ingesta-resumen-tabla">
          <table>
            <thead>
              <tr>
                <th className="col-fila">Fila</th>
                <th className="col-registro">RUT</th>
                <th className="col-estado">Estado</th>
                <th className="col-obs">Observaciones</th>
              </tr>
            </thead>
            <tbody>
              {detalle.map((fila: any) => (
                <tr key={fila.fila}>
                  <td className="col-fila">{fila.fila}</td>
                  <td className="col-registro">{fila.registro ?? "—"}</td>
                  <td className="col-estado">
                    <span className={`ingesta-estado ${tonoEstado(fila.estado)}`}>
                      {fila.estado}
                    </span>
                  </td>
                  <td className="col-obs">{fila.observacion ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="ingesta-leyenda">
          Los candidatos ya están disponibles en el proceso. Las filas con
          observación se guardaron, pero necesitan revisión.
        </p>
      </div>
    );
  };

  /* ---------------------------------------------------------------- pie -- */

  const pie = () => {
    if (paso === 1) {
      return (
        <>
          <button className="modal-button cancel" onClick={cerrar}>
            Cerrar
          </button>
          <Button
            className={archivo ? "button-cargarN" : "button-cargarN deshabilitado"}
            disabled={!archivo || isLoading}
            onClick={analizar}
          >
            Analizar archivo
          </Button>
        </>
      );
    }

    if (paso === 2) {
      const cargables: number =
        (analisis?.resumen?.total ?? 0) - (analisis?.resumen?.rejected ?? 0);

      return (
        <>
          <button className="modal-button cancel" onClick={reiniciar}>
            Volver
          </button>
          <Button
            className={
              faltantes.length || cargables === 0 || !procesoDestino || !reclutador || !correoValido(reclutador)
                ? "button-cargarN deshabilitado"
                : "button-cargarN"
            }
            disabled={faltantes.length > 0 || cargables === 0 || !procesoDestino || !reclutador || !correoValido(reclutador) || isLoading}
            onClick={importar}
          >
            {`Importar ${cargables} candidato${cargables === 1 ? "" : "s"}`}
          </Button>
        </>
      );
    }

    return (
      <button className="modal-button cancel" onClick={cerrar}>
        Cerrar
      </button>
    );
  };

  const titulos: Record<Paso, string> = {
    1: "Importar base de datos por Excel",
    2: "Importar base de datos por Excel",
    3: "Resumen de la importación",
  };

  return (
    <Dialog
      header={
        <div>
          <label className="titleInCargaNomina">{titulos[paso]}</label>
          <div className="ingesta-migas">
            {([1, 2, 3] as Paso[]).map((n: Paso) => (
              <span
                key={n}
                className={`ingesta-miga ${
                  paso === n ? "actual" : paso > n ? "hecho" : ""
                }`}
              >
                {n} · {["Archivo", "Configurar columnas", "Resumen"][n - 1]}
              </span>
            ))}
          </div>
        </div>
      }
      visible={uploadModalVisible}
      onHide={cerrar}
      style={{ width: "94vw", maxWidth: "1400px" }}
      closable={false}
      draggable={false}
      footer={" "}
    >
      {/* El contenido NO se desmonta mientras carga: se le superpone el spinner.
          Sacandolo del arbol, el cuerpo del modal se desplomaba a la altura del
          spinner y el pie con los botones subia y volvia a bajar en cada
          consulta. Con la capa encima el alto lo sigue fijando el contenido, y
          de paso quedan bloqueados los clics mientras dura la peticion. */}
      <div style={{ position: "relative" }} aria-busy={isLoading}>
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255, 255, 255, 0.72)",
            }}
          >
            <IconLoading
              iconInButom={false}
              className="container-icon-loading icon-loading"
              width="30"
              height="34"
            />
          </div>
        )}

        {paso === 1 && vistaArchivo()}
        {paso === 2 && vistaColumnas()}
        {paso === 3 && vistaResumen()}
      </div>

      <div className="modal-button-content registro-individual">{pie()}</div>
    </Dialog>
  );
};

export default CargaNominaIngesta;
